import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const NEAR_BOTTOM_THRESHOLD = 100;

/**
 * Pins a scroll container to the bottom while content grows, the way every
 * production chat (ChatGPT, Claude, Linear) does it:
 *
 *   1. Direct `scrollTop = scrollHeight - clientHeight` on the scroll
 *      container — instant, lands exactly at the bottom, no animation queue
 *      delay or "smooth-scroll startup latency" that `scrollIntoView({
 *      behavior: "smooth" })` ships with.
 *   2. A `ResizeObserver` on the scroll container *and* the inner content
 *      element catches every async size change (streamed text, image loads,
 *      web-font swaps, lazy-loaded children, killer-flow typewriter reveals
 *      growing the agent-step list) so the viewport stays pinned even when
 *      no React dep changes.
 *   3. A `useLayoutEffect` keyed on the explicit React deps still pins
 *      synchronously pre-paint on the first render of a chat and on every
 *      known reveal frame, so the first paint of a chat is *always* at the
 *      bottom rather than a post-paint scroll animation.
 *   4. `userScrolledUpRef` makes the pin sticky-but-respectful: once the
 *      user manually scrolls up the auto-pin pauses, and resumes only when
 *      they scroll back near the bottom (or `resetScroll()` is called on
 *      send, or `resetKey` changes to a new chat).
 *
 * Exported names preserved for back-compat with existing test mocks:
 * `scrollContainerRef`, `sentinelRef`, `handleScroll`, `resetScroll`,
 * `isScrolledUp`. `contentRef` is new — attach it to the element whose
 * height grows during streaming.
 */
export type UseAutoScrollOptions = {
  /**
   * When false, the viewport is not pinned as content grows — e.g. while an
   * agent response is streaming so the user can read earlier messages without
   * being pulled to the bottom on every token.
   */
  enabled?: boolean;
};

export function useAutoScroll(
  deps: unknown[],
  /**
   * When this value changes (e.g. the active chat id), sticky-up state
   * resets so the next layout effect jumps the new chat instantly to the
   * bottom, not wherever the previous chat happened to leave the scroll
   * position.
   */
  resetKey?: unknown,
  options?: UseAutoScrollOptions,
) {
  const enabled = options?.enabled ?? true;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  // Opt-in, caller-driven suppression (distinct from userScrolledUpRef, which
  // tracks manual scroll position). While paused, every growth-triggered pin
  // is a no-op so a user reading content near the bottom (e.g. a streaming
  // review checklist revealing items) is not yanked down. The position-based
  // userScrolledUpRef stays untouched, so resuming restores normal pinning.
  const isPausedRef = useRef(false);
  const lastObservedContentHeightRef = useRef(0);
  const prevResetKeyRef = useRef(resetKey);

  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey;
    userScrolledUpRef.current = false;
    lastObservedContentHeightRef.current = 0;
  }

  const setAutoScrollPaused = useCallback((paused: boolean) => {
    isPausedRef.current = paused;
  }, []);

  const syncScrollStickyState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return false;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    userScrolledUpRef.current = !nearBottom;
    return userScrolledUpRef.current;
  }, []);

  const pinToBottom = useCallback(() => {
    // Caller-driven pause (e.g. user reading a streaming review checklist):
    // skip the pin entirely without disturbing position-based sticky state.
    if (isPausedRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (!enabled) {
      syncScrollStickyState();
      return;
    }
    if (userScrolledUpRef.current) return;
    // Direct write — no animation, no queue. scrollHeight already accounts
    // for the container's own top/bottom padding so this lands at the true
    // bottom of the scroll area, not short of it.
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [enabled, syncScrollStickyState]);

  const handleScroll = useCallback(() => {
    return syncScrollStickyState();
  }, [syncScrollStickyState]);

  const resetScroll = useCallback(() => {
    userScrolledUpRef.current = false;
  }, []);

  // Pin synchronously pre-paint on every React-known change. This is what
  // makes the first paint of a chat land at the bottom rather than animate
  // there post-paint.
  useLayoutEffect(() => {
    pinToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinToBottom, enabled, ...deps]);

  // ResizeObserver covers growth React can't see: streamed text landing,
  // <img>s loading, fonts swapping, agent-step rows expanding, the killer-
  // flow typewriter reveal pacing in new blocks. We observe BOTH the scroll
  // container (clientHeight changes — viewport resize, sibling panes
  // collapsing) and the inner content element (scrollHeight changes —
  // messages growing).
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const scroller = scrollContainerRef.current;
    if (!scroller) return;

    const ro = new ResizeObserver(() => {
      const content = contentRef.current;
      const contentHeight =
        content?.scrollHeight ?? scrollContainerRef.current?.scrollHeight ?? 0;
      const contentGrew = contentHeight > lastObservedContentHeightRef.current;
      lastObservedContentHeightRef.current = contentHeight;

      // Viewport-only resizes (e.g. the canvas pane opening and narrowing the
      // chat column) must not yank the scroll position — only new message
      // content growing should pin.
      if (!enabled || !contentGrew) {
        syncScrollStickyState();
        return;
      }
      pinToBottom();
    });
    ro.observe(scroller);
    const content = contentRef.current;
    if (content) ro.observe(content);

    return () => ro.disconnect();
  }, [enabled, pinToBottom, syncScrollStickyState]);

  return {
    scrollContainerRef,
    sentinelRef,
    contentRef,
    handleScroll,
    resetScroll,
    /**
     * Opt-in pause: while `true`, content growth no longer re-pins the
     * viewport to the bottom. Resume with `false`. Used to let users read a
     * streaming review checklist without being scrolled away.
     */
    setAutoScrollPaused,
    /** Exposed for testing — true when user has scrolled away from bottom. */
    get isScrolledUp() {
      return userScrolledUpRef.current;
    },
  };
}
